"use client";

import { useQuery } from "@tanstack/react-query";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { LiaSquareFullSolid } from "react-icons/lia";

import SectionHeader from "@/components/shared/SectionHeader";
import TitleTag from "@/components/shared/TitleTag";
import { Typography } from "@/components/shared/Typography";
import { getBlockTitle, getLabel } from "@/data/labels";
import { fetchConditionalNewsAPI } from "@/services/newsService";
import { TitleNewsBlockType } from "@/types/page";
import { getLocaleFromPathname } from "@/utils/getLocaleFromPath";
import { getCategoryListUrlFromArticle, getNewsDetailsUrl } from "@/utils/getNewsDetailsUrl";
import TitleNewsBlockSkeleton from "./TitleNewsBlockSkeleton";

export default function TitleNewsBlock({
  block,
  pageSlug,
}: {
  block: TitleNewsBlockType;
  pageSlug: string;
}) {
  const pathname = usePathname();
  const locale = getLocaleFromPathname(pathname);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const lang = (locale?.split("-")[0] || "en") as AppLocale;
  const { data, isFetching } = useQuery({
    queryKey: [
      "fetchNewsTitles",
      block?.id,
      pageSlug,
      block?.orderBy,
      block?.limit,
    ],
    queryFn: () => fetchConditionalNewsAPI({ block, pageSlug, limit: block?.limit || 4, lang }),
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target); // Once it's visible, stop observing
        }
      },
      {
        threshold: [0.1],
        rootMargin: "500px 0px 500px 0px",
      },
    );

    const currentSection = sectionRef?.current;

    if (currentSection) {
      observer.observe(currentSection);
    }

    return () => {
      if (currentSection) {
        observer.unobserve(currentSection);
      }
    };
  }, []);

  const listHref =
    block?.orderBy === "latest"
      ? `/${locale || "en-IN"}/all-news`
      : getCategoryListUrlFromArticle(data?.[0], locale || "en-IN");

  return (
    <section
      ref={sectionRef}
      style={
        block?.backgroundColor !== "#ffffff" ? { backgroundColor: block?.backgroundColor } : {}
      }
      className="px-2"
    >
      <div className="flex justify-between items-center w-full">
        <SectionHeader
          titleColor={block?.textColor !== "#121212" ? block?.textColor : undefined}
          title={getBlockTitle(block?.title, lang)}
          showLogo={false}
        />
        {listHref && (
          <Link href={listHref} className="underline dark:text-white text-sm flex-shrink-0">
            {getLabel(lang, "VIEW_MORE")}
          </Link>
        )}
      </div>
      <div className="mt-8 w-full min-w-0 -mx-2 md:mx-0">
        {!data || !isVisible || isFetching ? (
          <TitleNewsBlockSkeleton limit={block?.limit || 4} />
        ) : (
          <div
            className="flex gap-6 md:gap-12 overflow-x-auto overflow-y-hidden no-scrollbar scroll-smooth pl-2 pr-4 md:pl-0 md:pr-0 md:grid md:grid-cols-4 md:overflow-visible w-full"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            {data &&
              data?.map((article: any, index: number) => (
                <div
                  key={index}
                  className="flex-shrink-0 w-[280px] max-w-[85vw] md:flex-shrink md:w-auto md:max-w-none"
                >
                  <div className="flex items-center w-full gap-3 mb-4 min-w-0 md:min-w-max">
                    <Typography
                      variant="body3"
                      className="uppercase whitespace-nowrap"
                      color="#FF0000"
                    >
                      {article.readTime}{" "}
                      {lang === "te"
                        ? "నిమిషం చదవండి"
                        : `${article.readTime === 1 ? "min" : "mins"} read`}
                    </Typography>
                    <span className="flex items-center w-full text-primaryRed">
                      <LiaSquareFullSolid size={10} />
                      <span className="w-full border-t border-dashed border-primaryRed dark:border-primaryRed"></span>
                    </span>
                  </div>
                  <Link
                    href={getNewsDetailsUrl(
                      article?.categoryId,
                      article?.subCategoryId,
                      article?.slug,
                      locale,
                    )}
                    aria-label={`View news: ${article?.title}`}
                  >
                    <Typography
                      color={block?.textColor !== "#121212" ? block?.textColor : undefined}
                      language={lang}
                      variant="h5"
                      className="line-clamp-3"
                    >
                      {article?.isLiveNews && article?.publishedAt && Date.now() - new Date(article.publishedAt).getTime() <= 12 * 60 * 60 * 1000 && <TitleTag labelKey="LIVE_NEWS" />}
                      {article?.isInPrint && <TitleTag labelKey="PRINT_EDITION" />}
                      {article?.title}
                    </Typography>
                  </Link>
                </div>
              ))}
          </div>
        )}
      </div>
    </section>
  );
}
